import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  buildQualityDashboardNavContext,
  getVisibleQualityDashboardModules,
  getVisibleQualityDashboardSubTabs,
  isSubTabActive,
  resolveActiveQualityDashboardModule,
} from "../config/qualityDashboardModules";
import { buildQualityTabNotificationsPath } from "../config/qualityDashboardNotifications";
import DashboardModuleTabBadge from "./DashboardModuleTabBadge";
import { useDashboardTabNotificationCounts } from "../hooks/useDashboardTabNotificationCounts";
import qaInspectorDashboardService from "../services/qaInspectorDashboard.service";

export function useQualityDashboardNav() {
  const [navEpoch, setNavEpoch] = useState(0);

  useEffect(() => {
    const bump = () => setNavEpoch((n) => n + 1);
    window.addEventListener("storage", bump);
    return () => window.removeEventListener("storage", bump);
  }, []);

  const navCtx = useMemo(() => buildQualityDashboardNavContext(), [navEpoch]);
  const modules = useMemo(() => getVisibleQualityDashboardModules(navCtx), [navCtx]);
  const moduleKeys = useMemo(() => new Set(modules.map((m) => m.key)), [modules]);

  return { navCtx, modules, moduleKeys };
}

export default function QualityDashboardModuleNav({ showSubTabs = true, className = "" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { navCtx, modules } = useQualityDashboardNav();
  const fetchCounts = useCallback(() => qaInspectorDashboardService.getTabNotificationCounts(), []);
  const tabCounts = useDashboardTabNotificationCounts({
    enabled: navCtx.canAccessQualityDashboard,
    fetchCounts,
    pathname: location.pathname,
  });

  const activeModule = useMemo(
    () => resolveActiveQualityDashboardModule(location.pathname, modules),
    [location.pathname, modules],
  );
  const subTabs = useMemo(
    () =>
      showSubTabs && activeModule?.key !== "overview"
        ? getVisibleQualityDashboardSubTabs(activeModule.key)
        : [],
    [showSubTabs, activeModule],
  );

  if (!navCtx.canAccessQualityDashboard) return null;
  if (modules.length === 0) return null;

  return (
    <div className={`buyer-dashboard__module-nav ${className}`.trim()}>
      <div className="buyer-dashboard__module-tabs">
        {modules.map((tab) => {
          const isOverview = tab.key === "overview";
          const isActiveModule = activeModule?.key === tab.key;
          const onOverviewPage =
            location.pathname === "/auction/qa/dashboard" ||
            location.pathname === "/auction/qa/dashboard/";

          const notificationCount = Number(tabCounts[tab.key]) || 0;
          const hasNotifications = notificationCount > 0;

          let stateClass = "";
          if (isOverview && onOverviewPage) {
            stateClass = " buyer-dashboard__module-tab--active";
          } else if (isActiveModule && !isOverview) {
            stateClass = " buyer-dashboard__module-tab--active-outline";
          } else if (isActiveModule && isOverview) {
            stateClass = " buyer-dashboard__module-tab--active";
          }
          if (hasNotifications) {
            stateClass += " buyer-dashboard__module-tab--has-notifications";
          }

          return (
            <Link
              key={tab.key}
              to={tab.to}
              className={`buyer-dashboard__module-tab${stateClass}`}
            >
              <span className="buyer-dashboard__module-tab-label">{tab.label}</span>
              <DashboardModuleTabBadge
                count={notificationCount}
                tabKey={tab.key}
                onNavigate={navigate}
                buildPath={buildQualityTabNotificationsPath}
              />
            </Link>
          );
        })}
      </div>

      {subTabs.length > 0 && (
        <div className="buyer-dashboard__sub-tabs">
          {subTabs.map((tab) => {
            const active = isSubTabActive(location.pathname + location.search, tab);
            return (
              <Link
                key={tab.key}
                to={tab.to}
                className={`buyer-dashboard__sub-tab${active ? " buyer-dashboard__sub-tab--active" : ""}`}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
